import type {
  Coverage,
  DateRange,
  Granularity,
  Meta,
  SeriesPoint,
  SourceMeta,
} from '../../src/api/queries';
import type { Source } from '../../src/source';
import { formatCount, formatDate, formatDayMonth } from './format';
import type { ViewState } from './useUrlState';

/**
 * What a chart's two ends — and its middle — are hiding. A bucket can be short, a window can run
 * past the collected corpus, and a stretch inside it can be collected but never read; `/api/series`
 * zero-fills all three, so a period nobody has paid to read arrives as a flawless line of zeros.
 * The bucket arithmetic is a deliberate twin of `bucketExprOn` in `queries.ts`, pinned by tests.
 */

/** What `/api/meta` publishes about coverage, or nothing while the request is in flight. */
export type CoverageFact = Coverage | undefined;

/**
 * A coverage-owned claim in its two code-owned forms: the caption a reader sees and the full
 * sentence its popover holds. The design styles both and reworders neither (spec ui-direction §4).
 */
export interface Claim {
  readonly short: string;
  readonly full: string;
}

// ─── Date arithmetic ────────────────────────────────────────────────────────

/** `YYYY-MM-DD` plus a whole number of days, in UTC — the zone every date on this site is in. */
export function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

function utcDay(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

/** The last day of the month `date` falls in. Day 0 of the next month, leap years included. */
function lastDayOfMonth(date: string): string {
  const [year, month] = date.split('-').map(Number);
  if (year === undefined || month === undefined) return date;
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

/** True when the date opens a bucket: any day for `day`, a Monday for `week`, the 1st for `month`. */
function startsBucket(date: string, g: Granularity): boolean {
  switch (g) {
    case 'day':
      return true;
    case 'week':
      return utcDay(date) === 1;
    case 'month':
      return date.endsWith('-01');
  }
}

/** True when the date closes a bucket: a Sunday for `week`, the month's last day for `month`. */
function endsBucket(date: string, g: Granularity): boolean {
  switch (g) {
    case 'day':
      return true;
    case 'week':
      return utcDay(date) === 0;
    case 'month':
      return date === lastDayOfMonth(date);
  }
}

/** The last day a bucket label covers — the other end of what `bucketExprOn` groups together. */
function bucketEnd(bucket: string, g: Granularity): string {
  switch (g) {
    case 'day':
      return bucket;
    case 'week':
      return addDays(bucket, 6);
    case 'month':
      return lastDayOfMonth(bucket);
  }
}

function overlaps(a: DateRange, b: DateRange): boolean {
  return a.from <= b.to && b.from <= a.to;
}

/** The days two ranges share. Meaningless unless they `overlaps`, so ask that first. */
function intersect(a: DateRange, b: DateRange): DateRange {
  return { from: a.from > b.from ? a.from : b.from, to: a.to < b.to ? a.to : b.to };
}

/** Whether the window holds any day the corpus has a report for. */
function touchesCorpus(state: ViewState, coverage: Coverage): boolean {
  const scraped = coverage.scraped ?? null;
  return scraped !== null && overlaps(scraped, { from: state.from, to: state.to });
}

// ─── Window coverage ────────────────────────────────────────────────────────

/**
 * What is known about the selected window: read, unread, part-read, or not yet knowable.
 *
 * `partial` carries the read parts *clipped to the window*, because that is what a note about the
 * window has to name. The states are deliberately exhaustive and deliberately include `unknown`:
 * while `/api/meta` is in flight nothing is known, and a page that guessed would flash a claim it
 * may have to take back a beat later.
 */
export type WindowCoverage =
  | { kind: 'unknown' }
  | { kind: 'empty' }
  | { kind: 'covered' }
  | { kind: 'partial'; ranges: DateRange[] }
  | { kind: 'uncovered' };

/**
 * Classify the selected window against the extracted runs.
 *
 * `empty` outranks the window question: on a corpus where nothing at all has been read there is no
 * window that could be part-read, and the site already has one sentence for that condition which
 * every surface shares.
 *
 * "Covered" is a walk rather than a `MIN..MAX` containment test, because the runs are not
 * contiguous — the live corpus has a four-day hole across the end of February 2026 (02-27…03-02)
 * and a one-day hole in August, and a window that swallows one is not a window whose zeros are
 * measurements.
 */
export function windowCoverage(state: ViewState, coverage: CoverageFact): WindowCoverage {
  if (coverage === undefined) return { kind: 'unknown' };
  const runs = coverage.extracted ?? [];
  if (runs.length === 0) return { kind: 'empty' };

  const window: DateRange = { from: state.from, to: state.to };
  const ranges = runs
    .filter((r) => overlaps(r, window))
    .map((r) => ({
      from: r.from < window.from ? window.from : r.from,
      to: r.to > window.to ? window.to : r.to,
    }));

  if (ranges.length === 0) return { kind: 'uncovered' };

  // The window is covered when the clipped runs leave no day of it behind. Runs arrive ascending
  // and disjoint, so one cursor is enough: a run that starts after the cursor is a hole.
  let cursor = window.from;
  for (const r of ranges) {
    if (r.from > cursor) return { kind: 'partial', ranges };
    if (r.to >= cursor) cursor = addDays(r.to, 1);
  }
  return cursor > window.to ? { kind: 'covered' } : { kind: 'partial', ranges };
}

/**
 * Whether a chart may draw a bucket at all.
 *
 * A bucket lying *entirely* outside every extracted run has no measurement behind it — `/api/series`
 * zero-fills it regardless — so the chart nulls it and leaves a visible gap. A bucket that overlaps
 * a run keeps its data: it may be short, which is what the partial-bucket note is for, and blanking
 * a January because its last three days are unread would hide 28 days of real work.
 *
 * While `/api/meta` is in flight everything is drawn: a gap is a claim too.
 */
export function bucketIsCovered(bucket: string, g: Granularity, coverage: CoverageFact): boolean {
  if (coverage === undefined) return true;
  const span: DateRange = { from: bucket, to: bucketEnd(bucket, g) };
  return (coverage.extracted ?? []).some((r) => overlaps(r, span));
}

/**
 * How many days of a bucket the source never published a report for.
 *
 * The fourth coverage state, at bucket resolution — see `Coverage.missing`. It is a *count* rather
 * than a predicate because that is the shape the honest answer takes: a month missing seven of its
 * thirty-one report days is neither a measurement nor a gap, it is a short month, and the only way
 * to say so is to say how short.
 *
 * Days outside the scraped span are not counted. They are absent for a different reason — nobody
 * has collected them yet — and `corpusEdgeNotes` already owns that sentence.
 */
export function missingDaysIn(bucket: string, g: Granularity, coverage: CoverageFact): number {
  if (coverage === undefined) return 0;
  const span: DateRange = { from: bucket, to: bucketEnd(bucket, g) };
  return (coverage.missing ?? []).reduce(
    (days, run) => (overlaps(run, span) ? days + dayCount(intersect(run, span)) : days),
    0,
  );
}

/**
 * Whether a bucket has no source document behind it — every day silent and no incident read out of
 * any of them. **Asked per series** (824 of the digest's 1,090 silent days are weekends its Monday
 * tally covers), and never true of a bucket with rows: the 2018-12 backfill dated its paragraphs
 * 20–25 December in a post published on the 27th (R19). The span is clipped to `scraped` first.
 */
export function bucketHasNoSource(
  bucket: string,
  g: Granularity,
  coverage: CoverageFact,
  incidentCount: number,
): boolean {
  if (incidentCount > 0) return false;
  const scraped = coverage?.scraped ?? null;
  if (coverage === undefined || scraped === null) return false;
  const span: DateRange = { from: bucket, to: bucketEnd(bucket, g) };
  if (!overlaps(scraped, span)) return false;
  const inCorpus = intersect(scraped, span);
  return missingDaysIn(bucket, g, coverage) >= dayCount(inCorpus);
}

/**
 * One series' own block of `/api/meta`, or `undefined` when the answer is not in yet. Everything at
 * the top level of `Meta` is corpus-wide, which is the wrong grain for a page reading one series.
 */
export function sourceMeta(meta: Meta | undefined, source: Source): SourceMeta | undefined {
  return meta?.sources?.find((entry) => entry.source === source);
}

/**
 * One series' coverage, falling back to the corpus-wide field. Every panel asks for its own page's
 * series — a chart that explained its gaps with the other corpus' backlog would be the mixing this
 * epic exists to stop — and the fallback keeps an `/api/meta` that predates `sources[]` honest.
 */
export function sourceCoverage(meta: Meta | undefined, source: Source): CoverageFact {
  return sourceMeta(meta, source)?.coverage ?? meta?.coverage;
}

/** The same answer without the fallback, for a series that may not take one: `Meta.coverage` is
 * the digest's, and the traffic page may not explain its gaps with the digest's backlog. */
export function seriesCoverage(meta: Meta | undefined, source: Source): CoverageFact {
  return sourceMeta(meta, source)?.coverage;
}

/** A `/api/series` point with the value the chart may actually plot — `null` where it may not. */
export type PlottedPoint = SeriesPoint & { value: number | null };

/** Which `SeriesPoint` fields are numbers a line can be drawn through. */
export type SeriesMetricKey = Exclude<keyof SeriesPoint, 'bucket'>;

/**
 * The chart's data, with unread buckets turned back into gaps — the rendering half of this module.
 *
 * It lives here rather than inline in `TimeSeriesChart` because it is the one piece of the fix that
 * decides what a reader *sees* rather than what they read, and an inline `.map()` in a component
 * that server rendering never draws is a line of code no test can hold onto: deleting it, or passing
 * the wrong granularity to it, changed nothing any assertion could see. As a function it is pinned
 * directly, and the chart's gap sentence is derived from its output rather than from the classifier,
 * so a chart that stopped nulling would also stop explaining the gaps it no longer has.
 */
export function plotPoints(
  points: readonly SeriesPoint[],
  metric: SeriesMetricKey,
  g: Granularity,
  coverage: CoverageFact,
  source: Source,
): PlottedPoint[] {
  // `bucketHasNoSource` is consulted for the traffic series alone, and the asymmetry is measured
  // rather than cautious — see the note on `bucketHasNoSource`.
  const silentIsAGap = source === 'traffic';
  return points.map((point) => {
    const drawn =
      bucketIsCovered(point.bucket, g, coverage) &&
      !(silentIsAGap && bucketHasNoSource(point.bucket, g, coverage, point.incident_count));
    return { ...point, value: drawn ? point[metric] : null };
  });
}

/**
 * Whether the corpus has been scraped but never extracted — the state this deployment starts in.
 *
 * Extraction is the step that costs money and is gated on a key, so "nothing extracted yet" is not
 * an edge case here, it is the first thing anyone sees: posts and segments number in the tens of
 * thousands meanwhile. Every honesty note on the site is written for the *other* state and each one
 * inverts here — a chart of zeros says the zeros are "tegelik vastus, mitte puuduv andmestik",
 * precisely backwards — so this predicate exists to be asked before any of them speaks.
 *
 * It reads the extracted runs rather than `incidents`' date bounds: a run that produced only
 * `NOT_AN_INCIDENT` rows has still been read, and a single backdated row used to be enough to make
 * the old predicate call the whole corpus extracted.
 */
export function isUnextracted(coverage: CoverageFact): boolean {
  return coverage !== undefined && (coverage.extracted ?? []).length === 0;
}

// ─── The sentences ──────────────────────────────────────────────────────────

/**
 * Which numbers a surface is talking about — the incident rows, or the police's own daily tallies.
 *
 * The two panels sit on the same page and are filled by the same extraction runner from the same
 * segments, so they are unread together and their honesty notes are the same sentence. They are
 * *not* the same noun: "juhtumid" under a panel of police tallies would name the wrong thing, and
 * "the police published no numbers" is a different accusation from "no crimes happened".
 */
export type CoverageSubject = 'incidents' | 'aggregates';

const SUBJECTS: Record<CoverageSubject, { plural: string; partitive: string; notAbsence: string }> =
  {
    incidents: {
      plural: 'Juhtumid',
      partitive: 'juhtumeid',
      notAbsence: 'mitte kuritegude puudumine',
    },
    aggregates: {
      plural: 'Politsei päevanumbrid',
      partitive: 'politsei päevanumbreid',
      notAbsence: 'mitte see, et politsei poleks numbreid avaldanud',
    },
  };

function unextractedNote(subject: CoverageSubject): string {
  return `Andmestik on veel töötlemata — teated on kogutud, kuid ${SUBJECTS[subject].partitive} pole neist veel eraldatud. Siinsed nullid ei ole mõõtmistulemus.`;
}

/**
 * What every zero on the site means before extraction has run: not a measurement.
 *
 * One sentence, shared by the line chart, the tally panel and the feed, because a reader who moves
 * between them is being told about one condition and not three.
 */
export const UNEXTRACTED_NOTE = unextractedNote('incidents');

function uncoveredNote(subject: CoverageSubject): string {
  const s = SUBJECTS[subject];
  return `Selle vahemiku kohta kogutud teated on veel töötlemata — ${s.partitive} pole neist eraldatud, nii et siinne tühjus on töötlemata andmestik, ${s.notAbsence}.`;
}

/**
 * The same fact about one window — the third state, and the one the audit found unsayable.
 *
 * It has to agree with `UNEXTRACTED_NOTE` in substance (the reports exist; the incidents have not
 * been taken out of them) and differ from it only in scope, because a reader who changes the date
 * range must not be told two different stories about the same pipeline.
 *
 * "Selle vahemiku kohta kogutud teated", rather than "selle vahemiku teated": a window can reach
 * back before the corpus begins, where there is nothing collected to be unread, and this sentence
 * is then about the part that was collected. What lies before the first scraped day is a different
 * sentence with a different remedy — `corpusEdgeNotes` — and it is printed beside this one.
 */
export const UNCOVERED_NOTE = uncoveredNote('incidents');

/**
 * What the gaps in the line are, said once, wherever a chart nulls a bucket.
 *
 * The second sentence is the honest cost of drawing gaps, and it names **both** kinds of row that
 * fall into one. An earlier draft named only the backdated mentions, and those are not the majority.
 * At day granularity 99 rows of the live corpus lie outside every run, and they come three ways:
 * **58** from posts that are only partly read, standing next to a run (the four-day hole across
 * 2026-02-27…03-02 holds 38 of them — the Monday digest reporting that weekend is stored and not
 * fully extracted — 13 more sit on 2018-10-08, the day after the first run ends, and 7 elsewhere);
 * **36** dated back more than a day by a later post that mentioned them; and **5** dated onto their
 * own publication day at a run's right edge, where the digest that would report that day has not
 * been read. The partition counts a partly-read post's rows as partly-read *first*: 17 of the 58
 * are also backdated, so a rule that sorts by date arithmetic alone will put them in the second
 * bucket and read 53 there — same 99 rows, different cut. None of the three is that day's measured
 * record, which is why they are left out of the line rather than drawn as if they were.
 *
 * No count appears in the sentence itself: how many rows a gap hides depends on the bucket size the
 * reader has chosen — the same corpus hides 99 rows at day granularity and 26 at month, where the
 * wider buckets overlap the runs — and a number that is right for one chart is wrong for the next.
 */
export const SERIES_GAP_NOTE =
  'Töötlemata päevad on graafikul katkestatud joonena, mitte nullina. Nende päevade kohta võib andmestikus siiski leiduda üksikuid ridu — osaliselt töödeldud teadetest ja hilisemate teadete tagantjärele mainitud sündmustest —, kuid need ei ole nende päevade mõõdetud pilt ja graafikule neid ei joonistata.';

/**
 * The same sentence for a *listing*, where the rows are visible rather than hidden.
 *
 * A feed over an unread window is not always empty: 2025-04 holds 14 such rows across all
 * categories — a category page's feed shows its slice of them (3 on the theft page) — every one a
 * later post's retrospective mention, over a month whose own 13 digests have never been read. The
 * chart above it draws that month as a gap, and a list that says nothing would be read as the
 * month's crime record.
 */
export const UNREAD_FEED_NOTE =
  'Selle vahemiku kohta kogutud teated on veel töötlemata. Allpool ei ole seetõttu selle perioodi juhtumite kirje, vaid üksikud read, mis pärinevad osaliselt töödeldud teadetest või on hilisemates teadetes tagantjärele mainitud.';

/**
 * The listing's *other* note, for a window that is part read — which includes the default view.
 *
 * `UNREAD_FEED_NOTE` is true only where nothing was read. `DEFAULT_VIEW_STATE` runs from the corpus
 * start to today and classifies as `partial`, so that sentence would have printed above a
 * `date_desc` feed whose top rows are August 2026 — inside the last extracted run, fully read days —
 * telling the reader that the record in front of them is not a record and that those rows are
 * retrospective mentions. Both halves false, on the first screen of every category page.
 *
 * What is true of a part-read window is a different shape: the rows that are there are the real
 * record of the days they come from, and the unread stretches contribute *nothing* — not a zero, not
 * a row. So this sentence says that, and says it without claiming anything about which rows are
 * which, because the feed cannot know.
 */
export const PARTLY_READ_FEED_NOTE =
  'Osa valitud vahemikust on veel töötlemata. Läbi töötatud päevade read on nende päevade tegelik kirje; töötlemata päevade kohta siin ridu ei ole ja see ei tähenda, et neil päevadel juhtumeid ei olnud.';

/**
 * The short form, for a badge or a caption that has room for a clause rather than a paragraph.
 *
 * Same claim, same words where the words are shared: a reader moving from a badge to the chart
 * below it must not think they are being told about two different problems.
 */
export const UNREAD_CLAIM_NOTE = 'Selle vahemiku kohta kogutud teated on veel töötlemata';

/**
 * The short form of `PARTLY_READ_FEED_NOTE`, and the same distinction the feed already had.
 *
 * Without it a badge on a part-read window said the whole window was unprocessed — one badge away
 * from a figure that had just been extracted from reports which very much had been read.
 * `DEFAULT_VIEW_STATE` is `partial` on the live corpus, so that was the first screen of every page.
 *
 * It opens on the feed paragraph's own words, because it is the same condition; what it adds is
 * what the figure beside it covers. Deliberately no full stop: like `UNREAD_CLAIM_NOTE`, it is set
 * under a badge or appended to a caption's own sentence.
 */
export const PARTLY_READ_CLAIM_NOTE =
  'Osa valitud vahemikust on veel töötlemata — siin on arvestatud ainult läbi töötatud päevad';

/** The same fact where the date pickers stand: a range cannot be stated, so none is. */
export const UNEXTRACTED_COVERAGE_HINT =
  'Andmestik on veel töötlemata — kaetud vahemikku ei saa veel näidata.';

/** `24.09.2018 – 07.10.2018; 01.01.2026 – 26.02.2026`, in reading order. */
function rangeList(ranges: readonly DateRange[]): string {
  return ranges.map((r) => `${formatDate(r.from)} – ${formatDate(r.to)}`).join('; ');
}

/** Whether every collected day has been read — the only state that permits a bare coverage claim. */
function isWhollyExtracted(coverage: Coverage): boolean {
  const { scraped = null, extracted = [] } = coverage;
  if (scraped === null) return false;
  const first = extracted[0];
  return extracted.length === 1 && first !== undefined
    ? first.from <= scraped.from && first.to >= scraped.to
    : false;
}

/**
 * The sentence a surface owes its reader about the selected window, or `null` when it owes none.
 *
 * Shared by the line chart, the tally panel and the feed — the same reasoning as `UNEXTRACTED_NOTE`
 * being one string. A `covered` window gets nothing: its zeros are measurements and the surface is
 * free to say so.
 *
 * A window that does not touch the collected corpus at all gets nothing either, and that is not the
 * same silence: there are no reports there to be unread, so the sentence would claim a backlog that
 * does not exist. `corpusEdgeNotes` owns that window — a chart of 2015 is empty because nobody
 * collected 2015, which is answered by a scraper and not by an extraction run.
 */
export function coverageNote(
  state: ViewState,
  coverage: CoverageFact,
  subject: CoverageSubject = 'incidents',
): string | null {
  const cov = windowCoverage(state, coverage);

  // `empty` outranks the pre-corpus check below. On a corpus where nothing has been read, the
  // corpus-wide sentence is owed on every surface for every window — including one that predates
  // the first digest — or the feed falls back to "juhtumeid ei leitud", the exact claim this whole
  // module exists to prevent on a fresh deployment.
  if (cov.kind === 'empty') return unextractedNote(subject);

  // A window that does not touch the collected corpus at all: there are no reports there to be
  // unread, so this sentence would claim a backlog that does not exist. `corpusEdgeNotes` owns that
  // window — a chart of 2015 is blank because nobody collected 2015, which a scraper answers and an
  // extraction run does not.
  if (coverage !== undefined && !touchesCorpus(state, coverage)) return null;

  switch (cov.kind) {
    case 'unknown':
    case 'covered':
      return null;
    case 'uncovered':
      return uncoveredNote(subject);
    case 'partial':
      return `${SUBJECTS[subject].plural} on eraldatud ainult osast valitud vahemikust (${rangeList(cov.ranges)}) — ülejäänud päevade kohta kogutud teated on veel töötlemata, nii et neid päevi ei ole mõõdetud.`;
  }
}

/**
 * Whether the selected window is a measurement — the guard on every affirmative sentence.
 *
 * "Ükski teade ei nimetanud kahjusummat" and "null tähendab, et ükski selle perioodi teade ei
 * nimetanud kannatanuid" are the same claim as `ZERO_NOTES` in different clothes: both assert that
 * the reports were read and said nothing. They sit on badges and captions rather than under a
 * chart, which is how they survived the first pass of this fix.
 *
 * `unknown` is deliberately *not* measured. A badge that claims while `/api/meta` is in flight has
 * to take the claim back a beat later, and these call sites can say nothing for that beat.
 */
export function isMeasured(state: ViewState, coverage: CoverageFact): boolean {
  return windowCoverage(state, coverage).kind === 'covered';
}

/**
 * A sentence that only a read window may make, one of two replacements, or nothing at all.
 *
 * Four ways, not two. The one that is easiest to miss is silence: while `/api/meta` is in flight
 * *no* sentence is known to be true, so a call site that has room to say nothing says nothing. A
 * caption that asserted "these reports are unprocessed" on first paint and swapped it for "no report
 * named a victim" a beat later would be wrong twice for the price of one.
 *
 * The one that was missing is `partial`, which the feed's two notes have had since D1: a part-read
 * window is not an unread one, and `DEFAULT_VIEW_STATE` is part-read on this corpus. Collapsing the
 * two back together prints "the reports here are unprocessed" beside a count taken out of reports
 * that were read — conservative, and still a sentence contradicting the number next to it.
 *
 * `empty` keeps the unread wording rather than gaining a third: on a corpus where nothing has been
 * read there is no part-read window, and that state has its own corpus-wide sentence elsewhere.
 */
export function measuredClaim(
  state: ViewState,
  coverage: CoverageFact,
  measured: string,
  unread: string = UNREAD_CLAIM_NOTE,
  partly: string = PARTLY_READ_CLAIM_NOTE,
): string | null {
  switch (windowCoverage(state, coverage).kind) {
    case 'unknown':
      return null;
    case 'covered':
      return measured;
    case 'partial':
      return partly;
    default:
      return unread;
  }
}

/**
 * The same states where the caller must hand back a string: a badge's note.
 *
 * The empty string is safe here and only here: it stands for the in-flight beat, and the badge it
 * belongs to is showing an em dash or a bare count rather than a euro total, so there is no figure
 * whose denominator has gone missing (`StatBadge`'s type rule is about money on screen).
 */
export function absentFigureNote(
  state: ViewState,
  coverage: CoverageFact,
  measured: string,
): string {
  return measuredClaim(state, coverage, measured) ?? '';
}

/** The one wording for "the reports named no sum", so three pages cannot drift into three. */
export const NO_DAMAGE_STATED = 'Ükski teade ei nimetanud kahjusummat';

/**
 * The listing's note, or `null` when the rows are the window's own record.
 *
 * Two states, two sentences: a window nothing was read for lists only what later reports mentioned,
 * and a part-read window lists a real record with unread stretches missing from it. Only for a
 * listing that *has* rows — an empty one is answered by `coverageNote`, whose sentence is about a
 * tühjus that both of these would contradict.
 */
export function unreadFeedNote(state: ViewState, coverage: CoverageFact): string | null {
  switch (windowCoverage(state, coverage).kind) {
    case 'uncovered':
      return UNREAD_FEED_NOTE;
    case 'partial':
      return PARTLY_READ_FEED_NOTE;
    default:
      return null;
  }
}

/**
 * The sentence under the date pickers, or `null` when the honest answer is to say nothing yet.
 *
 * The control needs *some* pair of bounds to put on its `<input type="date">` limits, and falls
 * back to `CORPUS_START`/today when `/api/meta` has not answered. Those fallbacks are fine as limits
 * and unusable as a claim: printing them produced a coverage range for a database covering nothing.
 *
 * The claim itself is built from `Meta.coverage`, never from `incidents`' own `date_min`/`date_max`.
 * Those bounds said "Andmed katavad 09.04.2025 – 11.08.2026" on the live corpus — a coverage claim
 * off 25 backdated rows, over a year whose 233 daily reports have never been read.
 */
export function coverageHint(coverage: CoverageFact): string | null {
  if (coverage === undefined) return null;
  const { scraped = null, extracted = [] } = coverage;
  if (scraped === null || extracted.length === 0) return UNEXTRACTED_COVERAGE_HINT;

  const collected = collectedSpan(scraped);
  return isWhollyExtracted(coverage)
    ? `${collected} ja ${extractionClause(coverage)}.`
    : `${collected}. ${asSentence(extractionClause(coverage))}`;
}

/** The clause both coverage sentences open with, so the two cannot drift and the composed
 * paragraph below can state it once. */
function collectedSpan(scraped: DateRange): string {
  return `Teated on kogutud ${formatDate(scraped.from)} – ${formatDate(scraped.to)}`;
}

/** How much of the collected span has been read — a clause, so its punctuation is the caller's. */
function extractionClause(coverage: Coverage): string {
  return isWhollyExtracted(coverage)
    ? 'juhtumid on neist kõigist eraldatud'
    : `juhtumid on neist eraldatud ainult vahemikes ${rangeList(coverage.extracted ?? [])} — mujal on andmestik veel töötlemata`;
}

/** The days inside the span the source published nothing on — the other clause, same treatment. */
function silenceClause(coverage: Coverage): string {
  const { scraped = null, missing = [] } = coverage;
  const silent = missing.reduce((days, run) => days + dayCount(run), 0);
  if (silent === 0) return 'igal selle vahemiku päeval on politsei teate avaldanud';
  const total = scraped === null ? 0 : dayCount(scraped);
  return `nendest ${formatCount(total)} päevast ${formatCount(silent)} kohta ei avaldanud politsei ühtegi teadet — need päevad ei ole siin nullid, vaid tühjad kohad`;
}

function asSentence(clause: string): string {
  return `${clause.slice(0, 1).toUpperCase()}${clause.slice(1)}.`;
}

/**
 * One series' coverage as one paragraph, with the span stated once (ux-spec §6).
 *
 * `/metoodika` printed {@link collectedSpanNote} and {@link coverageHint} back to back, and both
 * open on the same clause — so every source block told the reader when collection started and
 * ended twice, in two sentences that then went on to say different things. Both of those things
 * survive here; only the repetition does not.
 */
export function coverageParagraph(coverage: CoverageFact): string | null {
  if (coverage === undefined) return null;
  const { scraped = null, extracted = [] } = coverage;
  if (scraped === null) return UNEXTRACTED_COVERAGE_HINT;
  // Nothing read is not a range that can be narrowed — the span and its silence still hold, and
  // the third sentence says the read range cannot be drawn rather than drawing an empty one.
  const read =
    extracted.length === 0 ? UNEXTRACTED_COVERAGE_HINT : asSentence(extractionClause(coverage));
  return `${collectedSpan(scraped)}. ${asSentence(silenceClause(coverage))} ${read}`;
}

/** Days in an inclusive span. */
function dayCount(range: DateRange): number {
  return Math.round((Date.parse(range.to) - Date.parse(range.from)) / 86_400_000) + 1;
}

/** How many days of the collected span have been read, clipping any run to that span. */
function coveredDays(coverage: Coverage): number {
  const scraped = coverage.scraped ?? null;
  if (scraped === null) return 0;
  let days = 0;
  for (const r of coverage.extracted ?? []) {
    if (!overlaps(r, scraped)) continue;
    days += dayCount({
      from: r.from < scraped.from ? scraped.from : r.from,
      to: r.to > scraped.to ? scraped.to : r.to,
    });
  }
  return days;
}

/**
 * The header's backlog notice: how big the backlog is, and how much of the corpus it leaves unread.
 *
 * The original had two sizes, discriminated on whether anything at all had been extracted, and the
 * steady-state one — "numbrid võivad viimaste päevade osas veel muutuda" — vouched for everything
 * older than the last few days over a corpus 90.8% unread. Replacing it with a "wholly extracted?"
 * test only moved the problem: `pending > 0` already implies not-wholly-extracted, so that branch
 * was unreachable, and two live posts stored without segments (11301, 12811) make it permanently
 * unreachable even at `pending = 0`.
 *
 * So the notice states the extent rather than classifying it. Days, not segments: a reader who has
 * just picked a date range needs to know how much of the calendar the backlog covers, and "2 879
 * päevast on läbi töötatud 231" is a sentence that stays true as the backlog shrinks, needs no
 * threshold, and cannot flatter the corpus the way a two-way branch did.
 */
export function backlogClaim(pending: number, coverage: CoverageFact): Claim | null {
  if (pending <= 0 || coverage === undefined) return null;
  const waiting = `${formatCount(pending)} teadet ootab veel töötlemist`;
  if (isUnextracted(coverage)) {
    return {
      short: `${formatCount(pending)} teadet töötlemata — graafikud on tühjad`,
      full: `${waiting} — ühtegi juhtumit pole veel eraldatud, nii et graafikud ja loendid on seni tühjad.`,
    };
  }
  const scraped = coverage.scraped ?? null;
  if (scraped === null) return null;
  return {
    short: `${formatCount(pending)} teadet töötlemata`,
    full: `${waiting} — andmestiku ${formatCount(dayCount(scraped))} päevast on juhtumid eraldatud ${formatCount(coveredDays(coverage))} päeva kohta, ülejäänud päevade kohta on graafikud katkestatud, mitte nullis.`,
  };
}

/** The full sentence on its own, for the surfaces that print it without a chip to hide it behind. */
export function backlogNotice(pending: number, coverage: CoverageFact): string | null {
  return backlogClaim(pending, coverage)?.full ?? null;
}

/**
 * The landing page's one line about the whole corpus.
 *
 * The front door printed "N juhtumit M pressiteatest, ajavahemikust 07.07.2018 – 11.08.2026" — the
 * same claim this fix deleted from the date pickers, in the place a first-time reader is most
 * likely to believe it. Three things were wrong with it: the range came from `incidents`' own
 * bounds, so its left edge was a backdated row and its span implied eight years of reading; `M` was
 * every stored post of every series, so it counted 1,848 digests and five traffic posts as read
 * when 153 had been; and nothing on the page said the difference existed.
 */
export function corpusClaim(incidents: number, coverage: CoverageFact): Claim | null {
  if (coverage === undefined) return null;
  const { scraped = null, posts, extracted_posts } = coverage;
  if (scraped === null || posts === 0) {
    return { short: 'Andmestik on veel kogumata.', full: 'Andmestik on veel kogumata.' };
  }

  const collected = `Kogutud on ${formatCount(posts)} politsei pressiteadet ${formatDate(scraped.from)} – ${formatDate(scraped.to)}`;
  if (extracted_posts >= posts) {
    return {
      short: `Kõik ${formatCount(posts)} kogutud pressiteadet on läbi loetud.`,
      full: `${formatCount(incidents)} juhtumit ${formatCount(posts)} politsei pressiteatest ${formatDate(scraped.from)} – ${formatDate(scraped.to)}.`,
    };
  }

  /*
   * "N juhtumit M läbi töötatud pressiteatest" was the wrong preposition for the same reason the
   * old line was wrong: it hands every incident to the fully-read posts, and 66 of the live corpus's
   * 2,802 come from posts that are only partly read. The two numbers are stated side by side
   * instead, each saying exactly what it counts.
   *
   * And "juhtumeid pole neist veel eraldatud" is only sayable when there are none — a corpus with no
   * *fully* read post can still hold incidents from partial reads, which is the same blind spot as
   * the global flag this whole module replaced.
   */
  if (incidents === 0) {
    return {
      short: 'Ühtegi juhtumit pole veel eraldatud — graafikud on tühjad.',
      full: `${collected}. Juhtumeid pole neist veel eraldatud, nii et graafikud ja loendid on seni tühjad.`,
    };
  }
  if (extracted_posts === 0) {
    return {
      short: 'Ükski pressiteade pole veel täielikult läbi loetud.',
      full: `${collected}. Ükski neist ei ole veel täielikult läbi töötatud: siinsed ${formatCount(incidents)} juhtumit pärinevad pooleliolevast tööst ega kata ühtegi päeva tervikuna.`,
    };
  }
  return {
    short: `Arvud põhinevad ${formatCount(extracted_posts)} läbi loetud pressiteatel ${formatCount(posts)}-st.`,
    full: `${formatCount(incidents)} juhtumit on seni eraldatud. ${collected}, neist on täielikult läbi töötatud ${formatCount(extracted_posts)} — ülejäänud on veel töötlemata, nii et need arvud katavad ainult loetud osa andmestikust.`,
  };
}

/** The full sentence on its own, for the surfaces that print it without a popover to fold it into. */
export function corpusSummary(incidents: number, coverage: CoverageFact): string | null {
  return corpusClaim(incidents, coverage)?.full ?? null;
}

/** Why a live counter stopped: the corpus fell behind, its window is part read, or no recent week
 * closed to measure a pace from. The verdict is `useLiveCounter`'s; the sentences are here. */
export type FreezeReason = 'lag' | 'gap' | 'rate';

/** One counter's anchor day, labelled — the shared caption names each day and what it covers. */
export interface CounterAsOf {
  label: string;
  asOf: string;
}

/** `a, b ja c` — Estonian list punctuation, so the caption reads as a sentence and not as a list. */
function joinEt(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} ja ${items.at(-1) ?? ''}`;
}

/** The caption a group of live counters may not be rendered without (ux-spec §3.4). It names every
 * anchor day, because one "seis" over two separately-read series would be wrong about one of them. */
export function counterClaim(entries: readonly CounterAsOf[]): Claim {
  const short = 'Andmed laekuvad kord ööpäevas · arvestuslik seis, mitte reaalajas.';
  const pace =
    'Numbrid tiksuvad viimaste nädalate keskmises tempos edasi — see on arvestuslik seis';
  const tail = 'põhjal, mitte reaalajas laekuvad juhtumid.';
  // Before the first response there is no day to name, and the claim still has to be printed: the
  // caption is what makes the figures beside it readable, so it may not wait for them.
  if (entries.length === 0) {
    return { short, full: `${pace} viimase läbi loetud päeva ${tail}` };
  }

  const byDay = new Map<string, string[]>();
  for (const entry of entries) {
    const labels = byDay.get(entry.asOf);
    if (labels === undefined) byDay.set(entry.asOf, [entry.label]);
    else labels.push(entry.label);
  }
  const clauses = [...byDay].map(
    ([asOf, labels]) => `${joinEt(labels)} kohta ${formatDayMonth(asOf)}`,
  );

  return { short, full: `${pace} ${joinEt(clauses)} ${tail}` };
}

/** What a counter says when its window holds nothing yet. Not `0`: a zero here would be a
 * measurement of Estonia, when what is being reported is that nothing has been read. */
export const COUNTER_NOTHING_READ = 'Selle aasta kohta pole veel ühtegi juhtumit loetud.';

/** A frozen counter's own line, which replaces its sub-line: what day the figure stands at, and
 * why it is not moving. `lag` borrows the backlog sentence rather than writing a second one. */
export function frozenCounterNote(
  asOf: string,
  reason: FreezeReason,
  pending: number,
  coverage: CoverageFact,
): string {
  const stood = `Seis ${formatDayMonth(asOf)}`;
  if (reason === 'lag') {
    const backlog = backlogNotice(pending, coverage);
    return backlog === null
      ? `${stood} · uuendused ootel — uuemaid teateid pole veel läbi töötatud.`
      : `${stood} · uuendused ootel — ${backlog}`;
  }
  if (reason === 'gap') {
    return `${stood} · selles vahemikus on läbi lugemata päevi, nii et arv ei tiksu edasi.`;
  }
  return `${stood} · hiljutist tempot ei saa hinnata, nii et arv ei tiksu edasi.`;
}

/** Estonian for the period a bucket covers, in the two cases where a bucket is not a day. */
const UNITS: Record<Exclude<Granularity, 'day'>, { boundary: string; whole: string }> = {
  week: { boundary: 'nädala', whole: 'terve nädal' },
  month: { boundary: 'kuu', whole: 'terve kuu' },
};

/**
 * Which end buckets cover a shorter period than their label claims — the *shape* half of the same
 * question {@link partialBucketNote} answers in words, so a chart that dims a bar and a sentence
 * that explains the dimming can never disagree about which bar it is.
 */
export function partialBuckets(state: ViewState): { first: boolean; last: boolean } {
  if (state.g === 'day') return { first: false, last: false };
  return { first: !startsBucket(state.from, state.g), last: !endsBucket(state.to, state.g) };
}

/**
 * The sentence about partial end buckets, or `null` when both ends sit on a boundary.
 *
 * Shared with `AggregateChart`: the police's daily tallies are bucketed by the same expression, so
 * a month bar built from twelve days of summaries is short for exactly the same reason.
 */
export function partialBucketNote(state: ViewState): string | null {
  if (state.g === 'day') return null;
  const unit = UNITS[state.g];
  const { first, last } = partialBuckets(state);

  if (first && last) {
    return `Osaline vahemik: valitud periood ei alga ega lõpe ${unit.boundary} piiril, nii et graafiku esimene ja viimane punkt katavad lühema aja kui ${unit.whole} ja on seetõttu madalamad — see ei ole tõus ega langus.`;
  }
  if (first) {
    return `Osaline vahemik: valitud periood ei alga ${unit.boundary} piiril, nii et graafiku esimene punkt katab lühema aja kui ${unit.whole} ja on seetõttu madalam — see ei ole tõus ega langus.`;
  }
  if (last) {
    return `Osaline vahemik: valitud periood ei lõpe ${unit.boundary} piiril, nii et graafiku viimane punkt katab lühema aja kui ${unit.whole} ja on seetõttu madalam — see ei ole tõus ega langus.`;
  }
  return null;
}

/**
 * The sentences about a window that reaches outside the *collected* corpus.
 *
 * These two are about scraping, not extraction: beyond them there is no report at all, and the
 * fix is a scraper run rather than an extraction run. They are built from `coverage.scraped` —
 * `MIN`/`MAX` over `posts.published_date`, shifted onto the report-day axis — because the bounds
 * they used to read, `MIN`/`MAX` over `incidents.effective_date`, are a statement about extraction
 * wearing a collection sentence's clothes: on the live corpus they claim collection began
 * 2018-07-07, a day no report was published on, and ended at the last extracted day.
 *
 * Only for charts over `incidents`: `AggregateChart` never zero-fills, so it has no false tail to
 * explain and does not use this half.
 */
export function corpusEdgeNotes(state: ViewState, coverage: CoverageFact): string[] {
  // Nothing extracted: there are no edges worth arithmetic, and the one thing worth saying is that
  // the chart is not a measurement. Silence here is what let the zero-note speak unchallenged.
  if (coverage === undefined) return [];
  if (isUnextracted(coverage)) return [UNEXTRACTED_NOTE];

  const notes: string[] = [];
  const scraped = coverage.scraped ?? null;
  if (scraped === null) return notes;

  /*
   * These sentences used to explain zeros, and there are no longer any zeros to explain: a day
   * outside every extracted run is nulled by `plotPoints`, so the regions they describe render as a
   * break in the line — and, for a window wholly outside the corpus, as a blank chart. A note
   * describing a flat zero line above a chart that has none is its own small dishonesty, and it
   * would teach a reader to read the *other* gaps as zeros too.
   */
  if (state.to < scraped.from) {
    return [
      `Valitud vahemik jääb tervenisti andmestikust välja: esimene kogutud päev on ${formatDate(scraped.from)}, nii et graafikul ei ole midagi joonistada. See ei tähenda, et juhtumeid ei olnud — neist ei ole teateid kogutud.`,
    ];
  }
  if (state.from > scraped.to) {
    return [
      `Valitud vahemik jääb tervenisti kogutud andmestikust hilisemaks: viimane kogutud päev on ${formatDate(scraped.to)}, nii et graafikul ei ole midagi joonistada. Teated on veel kogumata, mitte juhtumid olemata.`,
    ];
  }

  if (state.from < scraped.from) {
    notes.push(
      `Andmestiku esimene päev on ${formatDate(scraped.from)} — sellest varasema aja kohta ei ole graafikul joont, sest teateid pole kogutud, mitte sellepärast, et juhtumeid ei olnud.`,
    );
  }
  if (state.to > scraped.to) {
    notes.push(
      `Andmestiku viimane päev on ${formatDate(scraped.to)} — graafiku parem serv on sellest hilisema aja kohta katkestatud seetõttu, et teated on veel kogumata, mitte et juhtumeid ei olnud.`,
    );
  }
  return notes;
}

/**
 * The collected span and the days inside it the source stayed silent (traffic page §2, §8.5).
 * The word "complete" never appears: the silent days are counted out of `missing`, not asserted about.
 */
export function collectedSpanNote(coverage: CoverageFact): string | null {
  const scraped = coverage?.scraped ?? null;
  if (coverage === undefined || scraped === null) return null;
  return `${collectedSpan(scraped)}. ${asSentence(silenceClause(coverage))}`;
}

/**
 * The days the source published nothing, said in the one way that cannot be mistaken for a backlog.
 *
 * **The wording is the whole point of this function.** Every other absence sentence on this site
 * ends in a promise: "veel töötlemata" means the reader is waiting for work that will be done. This
 * absence will never be filled — there is no report to read, and no amount of extraction spend
 * produces one — so it says `ei avaldatud` (was not published) and never `töötlemata`.
 *
 * Returns `null` rather than a reassuring sentence when the window has no silent days: a note
 * saying "0 days are missing" is noise on the 94 % of windows where nothing is missing at all.
 */
export function silentDaysNote(state: ViewState, coverage: CoverageFact): string | null {
  if (coverage === undefined) return null;
  const window: DateRange = { from: state.from, to: state.to };
  const days = (coverage.missing ?? []).reduce(
    (n, run) => (overlaps(run, window) ? n + dayCount(intersect(run, window)) : n),
    0,
  );
  if (days === 0) return null;
  return days === 1
    ? 'Ühel selle vahemiku päeval ei avaldanud politsei ühtegi teadet — see päev ei ole graafikul null, vaid tühi koht.'
    : `${formatCount(days)} selle vahemiku päeval ei avaldanud politsei ühtegi teadet — need päevad ei ole graafikul nullid, vaid tühjad kohad.`;
}

/**
 * Every note a `/api/series` chart owes its reader, in reading order.
 *
 * The order is the order the reader needs them in: what the shape of the line is (partial buckets),
 * what the line is missing and why (coverage), and only then what lies beyond the corpus's own
 * edges. The coverage sentence comes with `SERIES_GAP_NOTE` whenever it appears, because a reader
 * who is told the days are unread still has to be told what the break in the line means.
 *
 * Two sentences are deliberately withheld. Before extraction there is exactly one note: explaining
 * that the first bar of a chart with no data in it covers a shorter period than the rest, and is
 * "seetõttu madalam", would be arithmetic about nothing — and stacking it above the sentence that
 * says the chart is empty for a different reason entirely is how a reader ends up believing the
 * chart. The same holds for a window that is wholly unread: there is no line whose ends could be
 * partial.
 */
export function seriesEdgeNotes(
  state: ViewState,
  coverage: CoverageFact,
  gapped: boolean,
  source: Source,
): string[] {
  const cov = windowCoverage(state, coverage);
  if (cov.kind === 'empty') return [UNEXTRACTED_NOTE];

  // A window wholly outside the collected corpus draws nothing, and `corpusEdgeNotes` says so in
  // one sentence. The gap note must not be stacked on top of it: there are no "töötlemata päevad"
  // out there to explain, only days no report was ever collected for.
  if (coverage !== undefined && !touchesCorpus(state, coverage)) {
    return corpusEdgeNotes(state, coverage);
  }

  // A wholly unread window has no line whose ends could be a fragment of a month.
  const partial = cov.kind === 'uncovered' ? null : partialBucketNote(state);
  const note = coverageNote(state, coverage);
  // Above the gap sentence, because it changes what a gap means — and for the traffic series only.
  // On the digest it would report 1,090 silent days that are Fridays and Saturdays covered by
  // Monday's weekend tally. See `bucketHasNoSource`.
  const silent = source === 'traffic' ? silentDaysNote(state, coverage) : null;
  return [
    ...(partial === null ? [] : [partial]),
    ...(note === null ? [] : [note]),
    ...(silent === null ? [] : [silent]),
    // Read off the drawn data, not off the classifier: the sentence explains a break in the line,
    // so it is owed exactly when there is one. A window can be partly unread and still have no
    // gapped bucket — month bars that each overlap a run — and then there is nothing to explain.
    ...(gapped ? [SERIES_GAP_NOTE] : []),
    ...corpusEdgeNotes(state, coverage),
  ];
}
